import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SpinnerComponent } from '../../shared/components/spinner.component';
import { PaginationComponent } from '../../shared/components/pagination.component';
import { DashboardService, Lead } from '../../core/dashboard.service';
import { NotificationService } from '../../core/notification.service';

@Component({
  standalone: true,
  selector: 'app-leads-audit',
  imports: [CommonModule, FormsModule, SpinnerComponent, PaginationComponent],
  template: `
    <div class="bg-white p-6 rounded shadow">
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-3xl font-serif">Auditoria de Leads</h1>
        <div class="text-sm text-muted">
          Total: {{ meta.total || 0 }} leads
        </div>
      </div>

      <!-- Filtros -->
      <div class="mb-6 flex gap-3">
        <input type="date" [(ngModel)]="filters.dataInicio" placeholder="Data Início" class="px-3 py-2 border rounded">
        <input type="date" [(ngModel)]="filters.dataFim" placeholder="Data Fim" class="px-3 py-2 border rounded">
        <input type="text" [(ngModel)]="filters.noiva" placeholder="Buscar por noiva" class="px-3 py-2 border rounded flex-1">
        <button (click)="applyFilters()" class="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90">
          Filtrar
        </button>
        <button (click)="clearFilters()" class="px-4 py-2 border rounded hover:bg-surface">
          Limpar
        </button>
      </div>

      <!-- Tabela de Leads -->
      <div class="overflow-x-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-surface border-b">
              <th class="text-left p-3 text-sm font-medium">Data/Hora</th>
              <th class="text-left p-3 text-sm font-medium">Noiva</th>
              <th class="text-left p-3 text-sm font-medium">Contato</th>
              <th class="text-left p-3 text-sm font-medium">Para Fornecedor</th>
              <th class="text-left p-3 text-sm font-medium">Status</th>
              <th class="text-right p-3 text-sm font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let lead of leads" class="border-b hover:bg-surface/50">
              <td class="p-3 text-sm">{{ lead.criadoEm | date:'dd/MM/yyyy HH:mm' }}</td>
              <td class="p-3 text-sm font-medium">{{ lead.noiva }}</td>
              <td class="p-3 text-sm">
                <div>{{ lead.email }}</div>
                <div class="text-xs text-muted">{{ lead.telefone || '—' }}</div>
              </td>
              <td class="p-3 text-sm">{{ lead.fornecedorNome }}</td>
              <td class="p-3 text-sm">
                <span [class]="getStatusClass(lead.status)">
                  {{ lead.status }}
                </span>
              </td>
              <td class="p-3 text-sm text-right">
                <button (click)="viewMessage(lead)" class="text-xs px-2 py-1 border rounded hover:bg-surface mr-1">
                  Ver Mensagem
                </button>
                <button (click)="resendEmail(lead)" class="text-xs px-2 py-1 bg-primary text-white rounded hover:bg-primary/90">
                  Reenviar
                </button>
              </td>
            </tr>
            <tr *ngIf="leads.length === 0 && !loading">
              <td colspan="6" class="p-8 text-center text-muted">
                <div class="text-4xl mb-2">📭</div>
                <p>Nenhum lead encontrado</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginação -->
      <app-pagination
        *ngIf="meta.total > 0"
        [page]="page"
        [pageSize]="pageSize"
        [total]="meta.total || 0"
        (pageChange)="loadPage($event)"
      ></app-pagination>

      <!-- Loading -->
      <div *ngIf="loading" class="text-center py-8">
        <app-spinner></app-spinner>
      </div>
    </div>

    <!-- Modal para Ver Mensagem -->
    <div *ngIf="selectedLead as lead" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" (click)="closeModal()">
      <div class="bg-white rounded-lg p-6 max-w-2xl w-full mx-4" (click)="$event.stopPropagation()">
        <h2 class="text-2xl font-semibold mb-4">Mensagem do Lead</h2>
        <div class="mb-4">
          <p class="text-sm text-muted mb-1">De:</p>
          <p class="font-medium">{{ lead.noiva }} ({{ lead.email }})</p>
        </div>
        <div class="mb-4">
          <p class="text-sm text-muted mb-1">Para:</p>
          <p class="font-medium">{{ lead.fornecedorNome }}</p>
        </div>
        <div class="mb-4">
          <p class="text-sm text-muted mb-1">Data:</p>
          <p>{{ lead.criadoEm | date:'dd/MM/yyyy HH:mm' }}</p>
        </div>
        <div class="mb-6">
          <p class="text-sm text-muted mb-1">Mensagem:</p>
          <div class="p-4 bg-surface rounded border">
            {{ lead.mensagem }}
          </div>
        </div>
        <div class="flex justify-end gap-2">
          <button (click)="closeModal()" class="px-4 py-2 border rounded hover:bg-surface">
            Fechar
          </button>
          <button (click)="resendEmail(lead); closeModal()" class="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90">
            Reenviar E-mail
          </button>
        </div>
      </div>
    </div>
  `
})
export class LeadsAuditComponent implements OnInit {
  leads: Lead[] = [];
  page = 1;
  pageSize = 20;
  meta: any = {};
  loading = false;
  selectedLead: Lead | null = null;

  filters = {
    dataInicio: '',
    dataFim: '',
    noiva: ''
  };

  constructor(
    private dashboardSvc: DashboardService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadLeads();
  }

  loadLeads() {
    this.loading = true;
    const filters: any = { ...this.filters };
    
    this.dashboardSvc.getLeads(this.page, this.pageSize, filters).subscribe({
      next: (res) => {
        this.leads = res.data || [];
        this.meta = res.meta || {};
        this.loading = false;
      },
      error: (err) => {
        console.error('[LeadsAudit] Erro ao carregar leads', err);
        this.notification.error('Erro ao carregar leads');
        this.loading = false;
      }
    });
  }

  loadPage(page: number) {
    this.page = page;
    this.loadLeads();
  }

  applyFilters() {
    this.page = 1;
    this.loadLeads();
  }

  clearFilters() {
    this.filters = { dataInicio: '', dataFim: '', noiva: '' };
    this.page = 1;
    this.loadLeads();
  }

  viewMessage(lead: Lead) {
    this.selectedLead = lead;
  }

  closeModal() {
    this.selectedLead = null;
  }

  resendEmail(lead: Lead) {
    this.loading = true;
    this.dashboardSvc.resendLeadEmail(lead.id).subscribe({
      next: () => {
        this.notification.success('E-mail reenviado com sucesso');
        this.loadLeads();
      },
      error: (err) => {
        console.error('[LeadsAudit] Erro ao reenviar e-mail', err);
        this.notification.error('Erro ao reenviar e-mail');
        this.loading = false;
      }
    });
  }

  getStatusClass(status: string): string {
    const classes: any = {
      'enviado': 'px-2 py-1 bg-green-100 text-green-800 rounded text-xs',
      'pendente': 'px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs',
      'erro': 'px-2 py-1 bg-red-100 text-red-800 rounded text-xs',
      'lido': 'px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs'
    };
    return classes[status] || 'px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs';
  }
}
